import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';
import { variantConfig } from '../config/appVariant';
import styles from './Onboarding.module.css';

// What each tool's step points at, by helper id. Elements are tagged with
// data-tour="…" where they render; a step falls back to the tool's sidebar
// entry (nav-<id>), then to a centred card, when its target isn't on screen.
const PAGE_TARGET = {
  assignments: 'feature-tabs',
  analytics: 'feature-tabs',
  'content-generator': 'tool-tabs',
  tasks: 'tasks-composer',
  document: 'doc-input',
};

const MARGIN = 16; // card ↔ viewport edge
const GAP = 14;    // card ↔ highlighted element
const PAD = 6;     // highlight ring ↔ element
const SETTLE_MS = 1500; // how long a step waits for its page before falling back

// The element's rect if the user can actually see it: rules out the collapsed
// sidebar (opacity 0) and the closed mobile drawer (translated off-screen).
const visibleRect = (el) => {
  if (!el || el.checkVisibility?.({ checkOpacity: true }) === false) return null;
  const r = el.getBoundingClientRect();
  const onScreen = r.width > 0 && r.height > 0
    && r.right > 0 && r.bottom > 0 && r.left < window.innerWidth && r.top < window.innerHeight;
  return onScreen ? r : null;
};

const Tour = ({ onClose, setSidebarOpen }) => {
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const dialogRef = useRef(null);
  const spotRef = useRef(null);
  const cardRef = useRef(null);
  const primaryRef = useRef(null);

  // The tools come from the variant config, so the tour always matches the sidebar.
  const toolText = (h) => {
    const key = `onboarding.tools.${h.id}`;
    const text = t(key);
    return text === key ? (isRTL ? h.descAr : h.descEn) : text;
  };
  const steps = [
    {
      path: '/', targets: [],
      title: t('onboarding.welcomeTitle').replace('{{app}}', t('common.appName')),
      text: t('onboarding.welcomeText'),
    },
    { path: '/', sidebar: true, targets: ['nav'], title: t('onboarding.navTitle'), text: t('onboarding.nav') },
    { path: '/', targets: ['chat-input'], title: t('sidebar.home'), text: t('onboarding.chat') },
    {
      path: '/knowledge-bases', targets: ['kb-actions', 'nav-knowledge'],
      title: t('sidebar.knowledgeBases'), text: t('onboarding.knowledge'),
    },
    ...variantConfig.helpers.map((h) => ({
      path: h.path,
      targets: [PAGE_TARGET[h.id], `nav-${h.id}`].filter(Boolean),
      title: isRTL ? h.nameAr : h.nameEn,
      text: toolText(h),
    })),
    {
      path: '/feedback', targets: ['feedback-kinds', 'nav-feedback'],
      title: t('sidebar.feedback'), text: t('onboarding.feedback'),
    },
    { path: '/', sidebar: true, targets: ['account'], title: t('onboarding.accountTitle'), text: t('onboarding.account') },
  ];
  const step = steps[index];
  const isLast = index === steps.length - 1;
  const targetsRef = useRef(step.targets);
  targetsRef.current = step.targets;

  // Take the user to the step's page. `replace` keeps the tour out of the
  // back-button history. The sidebar is a closed drawer on phones, so steps
  // that point into it open it first.
  useEffect(() => {
    navigate(step.path, { replace: true });
    setSidebarOpen(Boolean(step.sidebar) && window.innerWidth <= 768);
  }, [step.path, step.sidebar, navigate, setSidebarOpen]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog.open) dialog.showModal();
    // Otherwise focus lands on Skip, and Enter would dismiss the tour.
    primaryRef.current?.focus();

    // ponytail: re-reads the target's position every frame while the tour is
    // open. That one loop covers lazy pages mounting late, the route and drawer
    // animations, resize and scroll; switch to observers if the tour ever
    // stays open for long. Targets below the fold are treated as absent
    // (no scrollIntoView), so tag elements that are visible on arrival.
    let frame;
    let first = true;
    let seenTargets = null;
    let changedAt = 0;
    const place = () => {
      frame = requestAnimationFrame(place);
      const spot = spotRef.current;
      const card = cardRef.current;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const cw = card.offsetWidth;
      const ch = card.offsetHeight;
      const clampX = (x) => Math.max(MARGIN, Math.min(x, vw - cw - MARGIN));
      const clampY = (y) => Math.max(MARGIN, Math.min(y, vh - ch - MARGIN));

      // A step's page takes a moment to mount. Until it has had that moment,
      // wait where we are for the step's own target instead of jumping to a
      // fallback (or the centre) and then again to the real one.
      const targets = targetsRef.current;
      if (targets !== seenTargets) {
        seenTargets = targets;
        changedAt = performance.now();
      }
      const settling = !first && performance.now() - changedAt < SETTLE_MS;

      let r = null;
      for (const [i, name] of targets.entries()) {
        if (settling && i > 0) break;
        r = visibleRect(document.querySelector(`[data-tour="${name}"]`));
        if (r) break;
      }
      if (!r && settling && targets.length) return;

      let box;  // highlight: left, top, width, height
      let at;   // card: left, top
      if (!r) {
        // Nothing to point at: a zero-size highlight leaves the whole page dimmed.
        box = [vw / 2, vh / 2, 0, 0];
        at = [(vw - cw) / 2, (vh - ch) / 2];
      } else {
        box = [r.left - PAD, r.top - PAD, r.width + 2 * PAD, r.height + 2 * PAD];
        const off = PAD + GAP;
        const centred = clampX(r.left + r.width / 2 - cw / 2);
        const beside = [[r.right + off, clampY(r.top)], [r.left - off - cw, clampY(r.top)]];
        const stacked = [[centred, r.bottom + off], [centred, r.top - off - ch]];
        const fits = ([x, y]) => x >= MARGIN && y >= MARGIN && x + cw <= vw - MARGIN && y + ch <= vh - MARGIN;
        // Tall targets (the sidebar) read best with the card beside them, wide
        // ones (tab bars, composers) with it below or above. Last resort: the
        // bottom of the screen, overlapping the target.
        at = (r.height > r.width ? [...beside, ...stacked] : [...stacked, ...beside]).find(fits)
          || [centred, vh - ch - MARGIN];
      }

      // No glide-in from the corner on the very first frame.
      spot.style.transition = card.style.transition = first ? 'none' : '';
      first = false;
      const [x, y, w, h] = box.map(Math.round);
      spot.style.left = `${x}px`;
      spot.style.top = `${y}px`;
      spot.style.width = `${w}px`;
      spot.style.height = `${h}px`;
      card.style.transform = `translate(${Math.round(at[0])}px, ${Math.round(at[1])}px)`;
    };
    place();
    return () => cancelAnimationFrame(frame);
  }, []);

  const close = () => dialogRef.current?.close();
  const progress = t('onboarding.progress')
    .replace('{{n}}', index + 1)
    .replace('{{total}}', steps.length);

  return (
    // onClose runs for Skip, Esc and the last step alike.
    <dialog ref={dialogRef} className={styles.dialog} onClose={onClose} aria-labelledby="onboarding-title">
      <div ref={spotRef} className={styles.spot} aria-hidden="true" />
      <div ref={cardRef} className={styles.card}>
        <div className={styles.head}>
          <span className={styles.count}>{progress}</span>
          <button type="button" className={styles.skip} onClick={close}>{t('onboarding.skip')}</button>
        </div>
        <div aria-live="polite">
          <h2 id="onboarding-title" className={styles.title}>{step.title}</h2>
          <p className={styles.text}>{step.text}</p>
        </div>
        <div className={styles.actions}>
          {index > 0 && (
            <button type="button" className={styles.back} onClick={() => setIndex(index - 1)}>{t('common.back')}</button>
          )}
          <button
            type="button"
            ref={primaryRef}
            className={styles.next}
            onClick={isLast ? close : () => setIndex(index + 1)}
          >
            {isLast ? t('onboarding.done') : t('common.next')}
          </button>
        </div>
      </div>
    </dialog>
  );
};

// First-login tour: walks the user through each page and highlights what the
// step is about. A native modal <dialog> gives the focus trap, Esc-to-skip and
// top-layer stacking for free, and keeps the page underneath inert meanwhile.
// Shown once per user per browser, and again from "Take a tour" in the account menu.
const Onboarding = ({ setSidebarOpen, replay }) => {
  const { currentUser } = useApp();
  const navigate = useNavigate();
  const seenKey = `onboardingSeen_${currentUser?.id}`;
  const [open, setOpen] = useState(() => {
    if (replay) return true;
    try { return !localStorage.getItem(seenKey); } catch (e) { return false; }
  });

  if (!open) return null;

  const handleClose = () => {
    try { localStorage.setItem(seenKey, '1'); } catch (e) { /* tour just shows again */ }
    setSidebarOpen(false);
    navigate('/', { replace: true });
    setOpen(false);
  };

  return <Tour onClose={handleClose} setSidebarOpen={setSidebarOpen} />;
};

export default Onboarding;
